import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getAccessToken } from '@/config/auth'
import toast from 'react-hot-toast'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add API key if configured
    const apiKey = import.meta.env.VITE_API_KEY
    if (apiKey) {
      config.headers['x-api-key'] = apiKey
    }

    // Add JWT auth token from localStorage
    const token = getAccessToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Track if we're already refreshing to avoid multiple refresh calls
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (error?: any) => void
}> = []

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })

  failedQueue = []
}

// Response interceptor to handle errors and auto-refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return apiClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Dynamically import to avoid circular dependency
        const { useAuthStore } = await import('@/stores/auth.store')

        // Try to refresh the token with timeout (10 seconds)
        const refreshPromise = useAuthStore.getState().refreshAuth()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Refresh token timeout')), 10000)
        )

        await Promise.race([refreshPromise, timeoutPromise])

        processQueue(null)
        isRefreshing = false

        // Retry the original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError)
        isRefreshing = false

        // Refresh failed - auth.store already cleared tokens and state
        // Just redirect to login page
        if (typeof window !== 'undefined') {
          window.location.href = '/login?session=expired'
          toast.error('Sua sessão expirou. Por favor, faça login novamente.')
        }

        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    if (error.response?.status === 403) {
      toast.error('Você não tem permissão para acessar este recurso.')
    } else if (error.response?.status === 404) {
      // Don't show toast for 404s, handle in the calling code
      console.warn('Resource not found:', error.config?.url)
    } else if (error.response?.status && error.response.status >= 500) {
      toast.error('Erro no servidor. Por favor, tente novamente mais tarde.')
    } else if (error.code === 'ERR_NETWORK') {
      toast.error('Erro de conexão. Verifique sua internet.')
    } else if (error.code === 'ECONNABORTED') {
      toast.error('A requisição demorou muito. Tente novamente.')
    }

    return Promise.reject(error)
  }
)

// Helper function to extract error message
export const getErrorMessage = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  if (error.response?.data?.error) {
    return error.response.data.error
  }
  if (error.message) {
    return error.message
  }
  return 'Ocorreu um erro inesperado'
}

export { apiClient }
export default apiClient
